import { styled } from "@mui/material/styles";
import { Paper } from "@mui/material";
import moment from "moment";
import { DATE_FORMAT } from "./constants";
import { DROPDOWN_SORT_KEYS, NO_SL_INTRO_DATE, SL_INTRO_DATE_TBD } from "../pages/Products/EditItem/Constants";

const toPascalCase = (str: string) => {
  if (str) {
    str = str.replace(/_/g, " ");
    str = str.toLowerCase();
    if (/^[a-z\d]+$/i.test(str)) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
    return str
      .replace(
        /([a-z\d])([a-z\d]*)/gi,
        (g0, g1, g2) => g1.toUpperCase() + g2.toLowerCase()
      )
      .replace(/[^a-z\d]/gi, " ");
  }
  return str;
};

const groupBy = (array: Array<any>, key: string) => {
  return array.reduce((result: any, currentValue: any) => {
    if (currentValue[key]) {
      (result[toPascalCase(currentValue[key])] =
        result[toPascalCase(currentValue[key])] || []).push(currentValue);
    }
    return result;
  }, {});
};

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const formatAmount = (amount: number, currency:string): string => {
  return amount.toLocaleString("en-US", { style: "currency", currency });
};

const renameObjKeys = (obj, newKeys) => {
  const keyValues = Object.keys(obj).map((key) => {
    const newKey = newKeys[key] || key;
    return { [newKey]: obj[key] };
  });
  return Object.assign({}, ...keyValues);
}

const getSessionInfo = () => {
  return JSON.parse(
    window?.localStorage.getItem("session") || "{}"
  );
};

const setTimeAgoStringFromDate = (timestamp) => {
  const now = new Date();
  const seconds = Math.floor((now.valueOf() - timestamp.valueOf()) / 1000);

  // Define time intervals in seconds
  const minute = 60;
  const hour = minute * 60;
  const day = hour * 24;

  if (seconds < minute) {
    return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
  } else if (seconds < hour) {
    const minutes = Math.floor(seconds / minute);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (seconds < day) {
    const hours = Math.floor(seconds / hour);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (seconds < day * 2) {
    // Less than 2 days ago, show "1 day ago"
    return `1 day ago`;
  } else {
    // For dates older than 1 day, return the date string
    const localDateFormat = moment.utc(timestamp).local().format('MM/DD/YYYY');
    return localDateFormat;
  }
}

/* 
  This function is used for custom sorting by key
  */
const sortByKey = (key = "name", isDesc = false) => {
  return (a: any, b: any) => {
    const valA = (isDesc ? b[key]?.toLowerCase?.() : a[key]?.toLowerCase?.()) ?? "";
    const valB = (isDesc ? a[key]?.toLowerCase?.() : b[key]?.toLowerCase?.()) ?? "";

    return valA.localeCompare(valB, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };
};

const isNonDateSlIntroDateFilterOption = (option: string | Date) => {
  const value = typeof option === "string" ? option : String(option);
  return value === SL_INTRO_DATE_TBD || value === NO_SL_INTRO_DATE;
};

const formatSlIntroDateFilterOption = (option: string | Date) => {
  if (!option) return "";
  if (isNonDateSlIntroDateFilterOption(option)) {
    return typeof option === "string" ? option : String(option);
  }
  const formatted = moment(option).format(DATE_FORMAT);
  return formatted === "Invalid date" ? String(option) : formatted;
};

const sortCVLFieldOptions = (data: any, descFields?: string[]) => {
  for (const [key, value] of Object.entries(data)) {
    const isDesc = !!descFields?.includes(key);
    if (Array.isArray(value) && value.length > 0) {
      if (typeof value[0] === "string") {
        data[key] = [...value].sort((a, b) => isDesc ? b.localeCompare(a) : a.localeCompare(b));
        // Sorted as an array of strings
      } else if (key === "dataFieldsTypes") {
        data.dataFieldsTypes = value.map((item) => ({
          ...item,
          types: Array.isArray(item.types)
            ? [...item.types].sort(sortByKey("description", isDesc))
            : item.types,
        }));
      } else if (key === "stageStatusTags") {
        data.stageStatusTags = value.map((item) => ({
          ...item,
          statuses: Array.isArray(item.statuses)
            ? [...item.statuses].sort(sortByKey("description", isDesc))
            : item.statuses,
          tags: Array.isArray(item.tags)
          ? [...item.tags].sort(sortByKey("name", isDesc))
          : item.tags,
        }));
      } else if (key === "attachmentTypes") {
        if (DROPDOWN_SORT_KEYS[key] in value[0]) {
          data[key] = [...value].sort(sortByKey(DROPDOWN_SORT_KEYS[key], isDesc));
        } else {
          data[key] = [...value].sort(sortByKey("name", isDesc));
        }
      } else {
        const sortKey = DROPDOWN_SORT_KEYS[key] ?? "name";
        data[key] = [...value].sort(sortByKey(sortKey, isDesc));
      }
    }
  }
};

const getInputTitle = (value: unknown): string | undefined => {
  if (typeof value !== "string" && typeof value !== "number") {
    return undefined;
  }
  const text = String(value).trim();
  return text || undefined;
};

export { groupBy, toPascalCase, Item, formatAmount, renameObjKeys, getSessionInfo, setTimeAgoStringFromDate, sortByKey, sortCVLFieldOptions, formatSlIntroDateFilterOption, getInputTitle };